import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetPDFVisibility } from 'api/elements/Models/ElementDto';
import { AtLeast } from 'common/types/Atleast';
import styles from './BudgetElementLine.module.scss';
import utilsStyles from '../../Utils.module.scss';
import { forwardRef, Ref, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LinesDataProvider } from '../../LinesDataProvider';
import { collectParameterIdByName, getFamilyParametersForBudgetLine, getExpressionParameters, getOrderedLineColumns, hasExistingFamilyLevelFormulas, isColumnVisible, LinesTableColumn, mapNewParametersToExisting, rewriteExpression, rewriteParametersExpressions } from '../../utils';
import InlineDropdownInput from 'common/components/inlineDropdownInput/InlineDropdownInput';
import InlineNumberInput from 'common/components/inlineNumberInput/InlineNumberInput';
import InlineTextInput from 'common/components/inlineTextInput/InlineTextInput';
import { Label } from 'common/components/texts/Texts';
import { useTranslation } from 'react-i18next';
import SearchableSelectInput from 'common/components/searchableSelectInput/SearchableSelectInput';
import SelectInput from 'common/components/selectInput/SelectInput';
import { Col, Form, Row } from 'react-bootstrap';
import FamiliesService from 'api/families/FamiliesService';
import Toast from 'common/services/Toast';
import Logger from 'common/services/Logger';
import { DEFAULT_DECIMAL_PLACES, LOGGER_LOG_TYPE } from 'Config';
import ElementsService from 'api/elements/ElementsService';
import { OptionTitleSubTitle, SearchableSelectTitleSubTitleOption } from 'common/components/searchableSelectInput/optionsFormats/OptionTitleSubTitle';
import BudgetLineColumn from '../../column/BudgetLineColumn';
import CustomTotalCell from '../../customTotalCell/CustomTotalCell';
import customTotalStyles from '../../customTotalCell/CustomTotalCell.module.scss';
import Button from 'common/components/button/Button';
import { FaAngleDown, FaAngleRight } from 'react-icons/fa';
import { SmallTabs, Tab } from 'common/components/smallTabs/SmallTabs';
import ConfigureTab from './configureTab/ConfigureTab';
import LineImagePicker from './lineImagePicker/LineImagePicker';
import PricesTab from './pricesTab/PricesTab';
import TreatmentsTab from './treatmentsTab/TreatmentsTab';

import { ParameterSectionBaseModel, ParameterSectionParameterType } from 'common/components/parametersSections/models';
import { BudgetLineParameterByElementDto, BudgetLineParameterDto } from 'api/budgets/models/BudgetLineParameterDto';
import InformationModal from 'common/components/modal/informationModal/InformationModal';
import YesNoModal from 'common/components/modal/yesNoModal/YesNoModal';
import DescriptionTab from './descriptionTab/DescriptionTab';
import AttachmentsTab from './attachmentsTab/AttachmentsTab';
import store from 'store/store';
import { Reducers } from 'store/types';
import { useDispatch, useSelector } from 'react-redux';
import MathUtils from 'common/services/MathUtils';
import { ElementSelectTitleSubTitleOptionDto } from 'common/components/configurableElementsLine/ConfigurableElementsLine';
import useNoInitialEffect from 'common/hooks/useNoInitialEffect';
import BudgetsService from 'api/budgets/BudgetsService';
import { updateLine, updateLineQuantity, calculateLineTotals, addLine, removeLineChildren } from 'store/budget/action';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import Utils from 'common/services/Utils';
import { LoadingSpinnerCenter } from 'common/components/loading/LoadingSpinner';
import { BudgetData, RECALCULATE_LINE_PARAMETER, SET_CALCULATING } from 'store/budget/type';
import ExpressionSystem from 'common/components/expressionButton/ExpressionSystem';
import { isTopLevelElementLine } from 'store/budget/reducer';

type TabKey = 'configure' | 'description' | 'treatments' | 'prices' | 'attachments';

interface Props {
    line: BudgetLineDto;
    disabled: boolean;
    level: number;
    isActive: boolean;
    isEditing: boolean;
    isComponentElement: boolean;
    isComponent: boolean;
    onChange: (line: AtLeast<BudgetLineDto, 'key'>, calculateTotals: boolean) => void;
    onAddedElement: () => void;
}

function BudgetElementLine({
    line, disabled, isActive, isEditing, level, isComponentElement, isComponent,
    onChange, onAddedElement,
}: Props, ref: Ref<HTMLDivElement>): JSX.Element | null {
    const { t } = useTranslation();
    const { taxes, units, hiddenColumns, columnsOrder, elementTypes } = useContext(LinesDataProvider);
    const orderedColumns = useMemo(() => getOrderedLineColumns(columnsOrder), [columnsOrder]);
    const [currentTab, setCurrentTab] = useState<TabKey>(isComponentElement ? 'description' : 'configure');
    const tabsOpen = line.tabsOpen ?? false;
    const [isLoading, setIsLoading] = useState(false);
    const [isCreateCustom, setIsCreateCustom] = useState(false);
    const [customElementTypeId, setCustomElementTypeId] = useState<string | null>(null);
    const [customElementName, setCustomElementName] = useState<string>('');
    const [showParametersQuantityInformation, setShowParametersQuantityInformation] = useState(false);
    const [keepFormulasPromise, setKeepFormulasPromise] = useState<{ resolve: (val: boolean) => void } | null>(null);
    const decimalPlaces = useSelector<Reducers, number>(x => x.budget.budget.decimalPlaces ?? DEFAULT_DECIMAL_PLACES);
    const isFirstElementQuery = useRef(true);

    const unitsOptions = useMemo(
        () => units.map(u => ({ selectedLabel: u.symbol, label: u.name, value: u.id })),
        [units]
    );
    const taxesOptions = useMemo(
        () => taxes.map(t => ({ selectedLabel: t.value?.toString() + ' %', label: t.description, value: t.id })),
        [taxes]
    );

    const showImageColumn = isColumnVisible(hiddenColumns, LinesTableColumn.ELEMENT_IMAGE);
    const showLineNumber = isColumnVisible(hiddenColumns, LinesTableColumn.LINE_NUMBER);
    const dispatch = useDispatch();
    const quantityParameters = useMemo(() => {
        const result: { key: string; name: string }[] = [];
        const collect = (param: BudgetLineParameterDto) => {
            if (param.parameterType === ParameterSectionParameterType.QUANTITY) {
                result.push({ key: param.key!, name: param.parameterName ?? '' });
            }
            param.children?.forEach(collect);
        };
        (line.parameters ?? []).forEach(collect);
        return result;
    }, [line.parameters]);
    const hasQuantityParameters = quantityParameters.length > 0;

    const [hasTreatmentSections, setHasTreatmentSections] = useState(false);

    // Check if this line should show the Treatments tab
    useEffect(() => {
        if (!isComponentElement && !isComponent) {
            setHasTreatmentSections(false);
            return;
        }

        // Quick check: already has sourceRequiresTreatment set on child sections
        const currentLines: BudgetLineDto[] = store.getState().budget.lines;
        const hasFlag = currentLines.some(l =>
            l.parentKey === line.key
            && l.lineType === BudgetLineType.SECTION
            && l.sourceRequiresTreatment
        );
        if (hasFlag) {
            setHasTreatmentSections(true);
            return;
        }

        // Backward compatibility: check the element's family for requiresTreatment
        const hasChildSections = currentLines.some(l =>
            l.parentKey === line.key && l.lineType === BudgetLineType.SECTION
        );
        if (!hasChildSections) return;

        let isMounted = true;

        // Get the element's family to check for requiresTreatment on PRODUCTS params
        const getFamilyParamsPromise = (): Promise<ParameterSectionBaseModel[] | undefined> => {
            if (line.elementId) {
                return BudgetsService.getElementParameters(line.elementId, false).then(elementData => {
                    if (!elementData.parameters?.length) return undefined;
                    const firstParam = elementData.parameters[0];
                    if (!firstParam.familyParameterId) return undefined;
                    return FamiliesService.getFamilyIdByParameter(firstParam.familyParameterId).then(familyId => {
                        if (!familyId) return undefined;
                        return FamiliesService.getFamilyParameters(familyId);
                    });
                });
            }
            // COMPONENT (custom): find familyId from line parameters
            const findFamilyParamId = (params: BudgetLineParameterDto[]): string | undefined => {
                for (const p of params) {
                    if (p.familyParameterId) return p.familyParameterId;
                    if (p.children?.length) {
                        const found = findFamilyParamId(p.children);
                        if (found) return found;
                    }
                }
                return undefined;
            };
            const familyParamId = findFamilyParamId(line.parameters ?? []);
            if (familyParamId) {
                return FamiliesService.getFamilyIdByParameter(familyParamId).then(familyId => {
                    if (!familyId) return undefined;
                    return FamiliesService.getFamilyParameters(familyId);
                });
            }
            return Promise.resolve(undefined);
        };

        getFamilyParamsPromise().then(famParams => {
            if (!isMounted || !famParams) return;
            const hasRequiresTreatment = famParams.some(p =>
                p.parameterType === ParameterSectionParameterType.PRODUCTS && p.requiresTreatment
            );
            if (hasRequiresTreatment) {
                setHasTreatmentSections(true);
                // Enrich sections with sourceRequiresTreatment for auto-treatment on new elements
                const productsParams = famParams.filter(p => p.parameterType === ParameterSectionParameterType.PRODUCTS);
                const sections = (store.getState().budget.lines as BudgetLineDto[]).filter(l =>
                    l.parentKey === line.key && l.lineType === BudgetLineType.SECTION && !l.sourceRequiresTreatment
                );
                for (const section of sections) {
                    const match = productsParams.find(p =>
                        (p.parameterName || p.title || '') === section.designation
                    );
                    if (match?.requiresTreatment) {
                        dispatch(updateLine({
                            key: section.key,
                            sourceFamilyParameterId: match.id || null,
                            sourceRequiresTreatment: true,
                        }));
                    }
                }
            }
        }).catch(() => { /* non-critical */ });

        return () => { isMounted = false; };
    }, [line.key, line.elementId]);

    const [highlightParameterKeys, setHighlightParameterKeys] = useState<string[]>([]);

    useEffect(() => {
        if (highlightParameterKeys.length > 0) {
            const timer = setTimeout(() => setHighlightParameterKeys([]), 2500);
            return () => clearTimeout(timer);
        }
    }, [highlightParameterKeys]);

    const onChangeValue = (newLine: Partial<BudgetLineDto>, calculateTotals: boolean) => {
        onChange({ key: line.key, ...newLine }, calculateTotals);
    }

    const onToggleOpen = () => {
        onChangeValue({ tabsOpen: !tabsOpen }, false);
    }

    const onChangeFamily = (v: string | null | undefined, option: SearchableSelectTitleSubTitleOption | null | undefined) => {
        onChangeValue({ tempFamily: option, tempElement: null }, false);
    }

    const onChangeElement = (v: string | null | undefined, option: SearchableSelectTitleSubTitleOption | null | undefined) => {
        onChangeValue({ tempElement: option }, false);
        if (option) {
            setIsCreateCustom(false);
        }
    }

    const onToggleCreateCustom = () => {
        const newValue = !isCreateCustom;
        setIsCreateCustom(newValue);
        if (newValue) {
            onChangeValue({ tempElement: null }, false);
        }
        setCustomElementTypeId(null);
        setCustomElementName('');
    }

    const onLoadFamilies = (
        inputValue: string,
        callback: (options: SearchableSelectTitleSubTitleOption[], hasMore: boolean) => void,
        _isStart: boolean,
        page: number
    ) => {
        FamiliesService.getList({
            hasParentId: false,
            isComponent: isComponentElement || isComponent,
            isElement: !isComponentElement && !isComponent,
            itemsPerPage: 10,
            page,
            name: inputValue,
            includeFamiliesAndSubFamilies: true,
        }).then(result => {
            const options: SearchableSelectTitleSubTitleOption[] = result.items.map(item => ({
                label: item.name,
                subTitle: item.parentName ?? '',
                value: item.id,
            }));
            callback(options, result.currentPage < result.totalPages);
        }).catch((error) => {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get families', error);
            Toast.error(t('messages.error_load_info'));
        });
    };

    const onLoadElements = (
        inputValue: string,
        callback: (options: SearchableSelectTitleSubTitleOption[], hasMore: boolean) => void,
        _isStart: boolean,
        page: number
    ) => {
        ElementsService.getElementsCatalogByFamilyId({
            itemsPerPage: 10,
            page,
            name: inputValue,
            familyId: line.tempFamily?.value ?? undefined,
            includeId: isFirstElementQuery.current && line.elementId ? line.elementId : undefined,
            isElement: !isComponentElement,
            isComponent: isComponentElement
        }).then(result => {
            const options: SearchableSelectTitleSubTitleOption[] = result.items.map(item => ({
                ...item,
                imageUrl: showImageColumn ? item.imageUrl : undefined,
                canShowNoImage: !!showImageColumn,
                data: item,
            }));

            if (isFirstElementQuery.current && !line.tempElement && line.elementId) {
                onChangeElement(line.elementId, options.find(x => x.value === line.elementId));
            }

            isFirstElementQuery.current = false;

            callback(options, result.currentPage < result.totalPages);
        }).catch((error) => {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get elements', error);
            Toast.error(t('messages.error_load_info'));
        });
    };

    const getFamilyParametersForCustomElement = getFamilyParametersForBudgetLine;

    const onAddCustomElement = async () => {
        const budget = store.getState().budget.budget;

        let parameters: BudgetLineParameterDto[] = [];
        let familyDescription: string | undefined;
        if (line.tempFamily?.value) {
            try {
                setIsLoading(() => true);
                const [params, familyData] = await Promise.all([
                    getFamilyParametersForCustomElement(line.tempFamily.value),
                    FamiliesService.getById(line.tempFamily.value),
                ]);
                parameters = params;
                familyDescription = familyData.description;
            } catch (error: any) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get family parameters', error);
                Toast.error(t('messages.error_load_info'));
            } finally {
                setIsLoading(() => false);
            }
        }

        const lineObj: Partial<BudgetLineDto> = {
            elementId: null,
            designation: customElementName,
            subDesignation: line.tempFamily?.label ?? '',
            grossUnitPrice: 0,
            unitId: null,
            taxId: budget.defaultTaxId ? budget.defaultTaxId : null,
            taxValue: budget.defaultTaxValue ? budget.defaultTaxValue : 0,
            discountPercentage: budget.defaultDiscountPercentage ? budget.defaultDiscountPercentage : 0,
            description: familyDescription || undefined,
            parameters,
            checkedForParameters: true,
            changedParameters: parameters.length > 0,
            isCustomElement: true,
            pdfVisibility: line.lineType === BudgetLineType.ELEMENT
                ? BudgetPDFVisibility.PRICE_INVISIBLE
                : BudgetPDFVisibility.ELEMENT_AND_PRICE_VISIBLE,
            elementTypeId: customElementTypeId,
            elementTypeName: customElementTypeId ? (elementTypes ?? []).find(et => et.id === customElementTypeId)?.name ?? null : null,
        };

        const allLines: BudgetLineDto[] = store.getState().budget.lines;
        const currentLine = allLines.find(l => l.key === line.key);
        if (currentLine && isTopLevelElementLine(currentLine, allLines)) {
            lineObj.discountPercentageExpression = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['budget', 'defaultDiscountPercentage']));
            lineObj.marginPercentageExpression = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['budget', 'defaultCombinedMarginPercentage']));
            lineObj.marginPercentage = (budget.defaultMarginPercentage ?? 0) + (budget.defaultCommissionMarginPercentage ?? 0);
            lineObj.decennialInsurancePercentageExpression = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['budget', 'defaultDecennialInsurancePercentage']));
            lineObj.decennialInsurancePercentage = budget.defaultDecennialInsurancePercentage ?? 0;
        }

        onChangeValue(lineObj, false);
        dispatch(calculateLineTotals(line.key));
        onAddedElement();
    }

    const onAddElement = async () => {
        if (isCreateCustom) {
            onAddCustomElement();
            return;
        }
        if (!line.tempElement) {
            return;
        }
        if (isComponentElement && line.elementId) {
            dispatch(removeLineChildren(line.key));
        }
        const elementData = (line.tempElement as any).data as ElementSelectTitleSubTitleOptionDto;
        const budget = store.getState().budget.budget;

        try {
            setIsLoading(() => true);

            const parametersData = await BudgetsService.getElementParameters(line.tempElement.value, true);

            let keepExistingFormulas = false;
            if (hasExistingFamilyLevelFormulas(line.parameters ?? [], parametersData.parameters ?? [])) {
                keepExistingFormulas = await new Promise<boolean>(resolve => setKeepFormulasPromise({ resolve }));
            }

            const lineObj: Partial<BudgetLineDto> = {
                imageUrl: parametersData.imageUrl,
                designation: parametersData.name,
                subDesignation: elementData.subTitle,
                elementId: parametersData.elementId,
                grossUnitPrice: parametersData.price ?? 0,
                unitId: parametersData.unitId ?? null,
                unitSymbol: parametersData.unitSymbol ?? null,
                taxId: budget.defaultTaxId ? budget.defaultTaxId : (parametersData.taxId ?? null),
                taxValue: budget.defaultTaxValue ? budget.defaultTaxValue : (parametersData.taxId ? taxes.find(x => x.id === parametersData.taxId)?.value ?? 0 : 0),
                description: parametersData.description,
                parameters: mapNewParametersToExisting(parametersData.parameters ?? [], line.parameters ?? [], keepExistingFormulas),
                checkedForParameters: true,
                pdfVisibility: parametersData.pdfVisibility,
                changedParameters: true,
                discountPercentage: budget.defaultDiscountPercentage ? budget.defaultDiscountPercentage : 0
            };

            const allLinesForCheck: BudgetLineDto[] = store.getState().budget.lines;
            const currentLineForCheck = allLinesForCheck.find(l => l.key === line.key);
            if (currentLineForCheck && isTopLevelElementLine(currentLineForCheck, allLinesForCheck)) {
                lineObj.discountPercentageExpression = ExpressionSystem.serializeParameterMentionId(
                    ExpressionSystem.formatParameterId(['budget', 'defaultDiscountPercentage']));
                lineObj.marginPercentageExpression = ExpressionSystem.serializeParameterMentionId(
                    ExpressionSystem.formatParameterId(['budget', 'defaultCombinedMarginPercentage']));
                lineObj.marginPercentage = (budget.defaultMarginPercentage ?? 0) + (budget.defaultCommissionMarginPercentage ?? 0);
            }

            // Rewrite CURRENT_PARENT_LINE references in child lines to match new parent parameters
            const oldParamMap = collectParameterIdByName(line.parameters ?? []);
            const newParamMap = collectParameterIdByName(lineObj.parameters as BudgetLineParameterDto[]);

            const idMapping = new Map<string, string>();
            oldParamMap.forEach((oldStrippedId, name) => {
                const newStrippedId = newParamMap.get(name);
                if (newStrippedId && oldStrippedId !== newStrippedId) {
                    idMapping.set(oldStrippedId, newStrippedId);
                }
            });

            if (idMapping.size > 0) {
                const allLines = store.getState().budget.lines;
                const childLines = allLines.filter((l: BudgetLineDto) => l.parentKey === line.key);
                for (const childLine of childLines) {
                    dispatch(updateLine({
                        key: childLine.key,
                        quantityExpression: rewriteExpression(childLine.quantityExpression, idMapping) as string | null,
                        discountPercentageExpression: rewriteExpression(childLine.discountPercentageExpression, idMapping) as string | null,
                        totalExcludingTaxExpression: rewriteExpression(childLine.totalExcludingTaxExpression, idMapping) as string | null,
                        totalUnitExcludingTaxExpression: rewriteExpression(childLine.totalUnitExcludingTaxExpression, idMapping) as string | null,
                        marginPercentageExpression: rewriteExpression(childLine.marginPercentageExpression, idMapping) as string | null,
                        grossUnitPriceExpression: rewriteExpression(childLine.grossUnitPriceExpression, idMapping) as string | null,
                        parameters: rewriteParametersExpressions(childLine.parameters, idMapping),
                        changedParameters: childLine.parameters?.length > 0 ? true : undefined,
                    }));
                }
            }

            onChangeValue(lineObj, false);
            dispatch(updateLineQuantity(line.key));
            dispatch(calculateLineTotals(line.key));
            // Evaluate parameter formulas so computed values are populated immediately
            dispatch({ type: SET_CALCULATING, calculating: true });
            dispatch({ type: RECALCULATE_LINE_PARAMETER, lineKey: line.key });

            // Auto-add treatments: search sibling elements in same section AND across all sections of parent COMPONENT_ELEMENT
            // Skip when changing element (line already had an elementId) — treatments already exist as children
            const isChangingElement = !!line.elementId;
            if (!isComponentElement && !isComponent && !isChangingElement) {
                const linesForTreatment: BudgetLineDto[] = store.getState().budget.lines;
                const treatmentMap = new Map<string, BudgetLineDto>();

                // Collect all element lines to search for treatments
                const parentSection = linesForTreatment.find(l => l.key === line.parentKey);
                let sectionsToSearch: BudgetLineDto[] = parentSection ? [parentSection] : [];

                // Also search other sections of the parent COMPONENT_ELEMENT
                if (parentSection?.parentKey) {
                    const grandParent = linesForTreatment.find(l => l.key === parentSection.parentKey);
                    if (grandParent && (grandParent.lineType === BudgetLineType.COMPONENT_ELEMENT || grandParent.lineType === BudgetLineType.COMPONENT)) {
                        sectionsToSearch = linesForTreatment.filter(l =>
                            l.parentKey === grandParent.key && l.lineType === BudgetLineType.SECTION
                        );
                    }
                }

                for (const section of sectionsToSearch) {
                    const elementsInSection = linesForTreatment.filter(l =>
                        l.parentKey === section.key
                        && l.key !== line.key
                        && (l.lineType === BudgetLineType.ELEMENT || l.lineType === BudgetLineType.COMPONENT_ELEMENT)
                    );
                    for (const elem of elementsInSection) {
                        const treatmentChildren = linesForTreatment.filter(l =>
                            l.parentKey === elem.key
                            && (l.lineType === BudgetLineType.ELEMENT || l.lineType === BudgetLineType.COMPONENT_ELEMENT)
                        );
                        for (const tc of treatmentChildren) {
                            const dedupeKey = tc.elementId || tc.designation;
                            if (dedupeKey && !treatmentMap.has(dedupeKey)) {
                                treatmentMap.set(dedupeKey, tc);
                            }
                        }
                    }
                }

                if (treatmentMap.size > 0) {
                    for (const treatment of Array.from(treatmentMap.values())) {
                        if (!treatment.elementId) continue;
                        try {
                            const treatmentData = await BudgetsService.getElementParameters(treatment.elementId, false);
                            const treatmentKey = Utils.newGuid();
                            dispatch(addLine(BudgetLineType.ELEMENT, 1, line.key, treatmentKey));
                            dispatch(updateLine({
                                key: treatmentKey,
                                imageUrl: treatmentData.imageUrl,
                                designation: treatmentData.name,
                                subDesignation: treatment.subDesignation ?? '',
                                elementId: treatmentData.elementId,
                                grossUnitPrice: treatmentData.price ?? 0,
                                unitId: treatmentData.unitId ?? null,
                                unitSymbol: treatmentData.unitSymbol ?? null,
                                taxId: budget.defaultTaxId ? budget.defaultTaxId : (treatmentData.taxId ?? null),
                                taxValue: budget.defaultTaxValue ? budget.defaultTaxValue : (treatmentData.taxId ? taxes.find(x => x.id === treatmentData.taxId)?.value ?? 0 : 0),
                                description: treatmentData.description,
                                parameters: mapNewParametersToExisting(treatmentData.parameters ?? [], []),
                                checkedForParameters: true,
                                pdfVisibility: treatmentData.pdfVisibility,
                                changedParameters: true,
                            }));
                            dispatch(calculateLineTotals(treatmentKey));
                        } catch { /* non-critical */ }
                    }
                }
            }

            if (isComponentElement && parametersData.children) {
                buildChildrenLines(parametersData.children, line.key, budget);

                // Set sourceRequiresTreatment on SECTION lines created from PRODUCTS parameters
                try {
                    const componentFamilyId = line.tempFamily?.value;
                    if (componentFamilyId) {
                        const famParams = await FamiliesService.getFamilyParameters(componentFamilyId);
                        const productsParams = famParams.filter(p => p.parameterType === ParameterSectionParameterType.PRODUCTS);
                        const currentLines: BudgetLineDto[] = store.getState().budget.lines;
                        const childSections = currentLines.filter(l => l.parentKey === line.key && l.lineType === BudgetLineType.SECTION);
                        for (const section of childSections) {
                            const matchingParam = productsParams.find(p =>
                                (p.parameterName || p.title || '') === section.designation
                            );
                            if (matchingParam) {
                                dispatch(updateLine({
                                    key: section.key,
                                    sourceFamilyParameterId: matchingParam.id || null,
                                    sourceRequiresTreatment: matchingParam.requiresTreatment ?? false,
                                }));
                            }
                        }
                    }
                } catch { /* non-critical */ }
            }
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get the element data', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            setIsLoading(() => false);
        }
        onAddedElement();
    }

    const buildChildrenLines = (children: BudgetLineParameterByElementDto[], parentKey: string, budget: BudgetData) => {
        children.forEach(element => {
            const key = Utils.newGuid()

            if (element.type === 'SECTION') {
                dispatch(addLine(BudgetLineType.SECTION, 1, parentKey, key));
                dispatch(updateLine({
                    key,
                    designation: element.name ?? '',
                }));
            } else {
                dispatch(addLine(element.isComponent ? BudgetLineType.COMPONENT_ELEMENT : BudgetLineType.ELEMENT, 1, parentKey, key));
                dispatch(updateLine({
                    key,
                    imageUrl: element.imageUrl,
                    designation: element.name ?? '',
                    subDesignation: element.subTitle ?? '',
                    elementId: element.elementId ?? '',
                    grossUnitPrice: element.price ?? 0,
                    quantity: element.quantity ?? 0,
                    unitId: element.unitId ?? null,
                    unitSymbol: element.unitSymbol ?? null,
                    taxId: budget.defaultTaxId ? budget.defaultTaxId : (element.taxId ?? null),
                    taxValue: budget.defaultTaxValue ? budget.defaultTaxValue : (element.taxId ? taxes.find(x => x.id === element.taxId)?.value ?? 0 : 0),
                    description: element.description,
                    parameters: mapNewParametersToExisting(element.parameters ?? [], []),
                    checkedForParameters: true,
                    pdfVisibility: element.pdfVisibility,
                    changedParameters: true,
                    discountPercentage: budget.defaultDiscountPercentage ? budget.defaultDiscountPercentage : 0
                }));
            }

            dispatch(calculateLineTotals(key))

            if (element.children) {
                buildChildrenLines(element.children, key, budget)
            }
        });
    }

    useNoInitialEffect(() => {
        isFirstElementQuery.current = true;
        setIsCreateCustom(false);
        setCustomElementTypeId(null);
        setCustomElementName('');
        onChangeValue({ tempElement: null, tempFamily: null }, false);
    }, [isEditing]);

    const cellClass = (extra: string) => `${utilsStyles.column} ${extra}`;

    const renderCell = (col: LinesTableColumn): JSX.Element | null => {
        switch (col) {
            case LinesTableColumn.DESIGNATION:
                return (
                    <BudgetLineColumn key={col} column={col} className={`${utilsStyles.column} ${utilsStyles.columnAlignLeft} ${utilsStyles.designationColumn} ${styles.designationColumn}`}>
                        <Button size='normal' onClick={onToggleOpen} className={styles.toggleOpenButton}>
                            {!tabsOpen && <FaAngleRight />}
                            {tabsOpen && <FaAngleDown />}
                        </Button>
                        {showImageColumn && (
                            <LineImagePicker
                                line={line}
                                disabled={disabled}
                                isElementLine={!isComponentElement && !isComponent && !!line.elementId}
                                onChange={patch => onChangeValue(patch, false)}
                            />
                        )}
                        <div className={styles.designationInformation}>
                            <div className={styles.designationWithLevel}>
                                {showLineNumber && <div className={styles.number}>{line.number}</div>}
                                <InlineTextInput
                                    value={line.designation}
                                    onBlur={v => onChangeValue({ designation: v ?? '' }, false)}
                                    className={styles.designation}
                                    placeholder={t('budgets.budget.lines.without_designation')}
                                    disabled={disabled}
                                />

                            </div>
                            <InlineTextInput
                                value={line.subDesignation}
                                onBlur={v => onChangeValue({ subDesignation: v ?? '' }, false)}
                                className={styles.information}
                                placeholder={line.isCustomElement ? t('budgets.budget.lines.element_family_placeholder') : t('budgets.budget.lines.without_information')}
                                disabled={disabled}
                            />
                        </div>
                    </BudgetLineColumn>
                );
            case LinesTableColumn.QUANTITY:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.quantityColumn)}>
                        <InlineNumberInput
                            value={line.quantity}
                            onBlur={v => onChangeValue({ quantity: v ?? 0, quantityExpression: v !== line.quantity ? null : line.quantityExpression }, true)}
                            className={'text-center'}
                            disabled={hasQuantityParameters || disabled}
                            onClick={hasQuantityParameters ? () => setShowParametersQuantityInformation(true) : undefined}
                            expressionButton={hasQuantityParameters
                                ? {
                                    title: '',
                                    value: '',
                                    loadParameters: () => [],
                                    onChange: () => {},
                                    onOpen: () => setShowParametersQuantityInformation(true)
                                }
                                : {
                                    title: ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.quantity')]),
                                    value: line.quantityExpression ?? '',
                                    loadParameters: () => getExpressionParameters(line.key),
                                    onChange: (expr, value) => onChangeValue({ quantity: value ?? 0, quantityExpression: expr }, true)
                                }}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.UNIT:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.unityColumn)}>
                        <InlineDropdownInput
                            value={line.unitId}
                            options={unitsOptions}
                            onChange={v => {
                                const unit = units.find(u => u.id === v);
                                onChangeValue({ unitId: v, unitSymbol: unit?.symbol ?? null }, false);
                            }}
                            className={'text-center'}
                            disabled={disabled}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.GROSS_UNIT_PRICE:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.grossUnityColumn)}>
                        <InlineNumberInput
                            value={line.grossUnitPrice}
                            onBlur={v => onChangeValue({ grossUnitPrice: v ?? 0, grossUnitPriceExpression: v !== line.grossUnitPrice ? null : line.grossUnitPriceExpression }, true)}
                            className={'text-center'}
                            type="money"
                            decimalScale={decimalPlaces}
                            disabled={disabled}
                            expressionButton={{
                                title: ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.gross_unit_price')]),
                                value: line.grossUnitPriceExpression ?? '',
                                loadParameters: () => getExpressionParameters(line.key),
                                onChange: (expr, value) => onChangeValue({ grossUnitPrice: value ?? 0, grossUnitPriceExpression: expr }, true)
                            }}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.DISCOUNT:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.discountColumn)}>
                        <InlineNumberInput
                            value={line.discountPercentage}
                            onBlur={v => onChangeValue({ discountPercentage: v ?? 0, discountPercentageExpression: v !== line.discountPercentage ? null : line.discountPercentageExpression }, true)}
                            className={'text-center'}
                            type="percentage"
                            disabled={disabled}
                            expressionButton={{
                                title: ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.discount')]),
                                value: line.discountPercentageExpression ?? '',
                                loadParameters: () => getExpressionParameters(line.key),
                                onChange: (expr, value) => onChangeValue({ discountPercentage: value ?? 0, discountPercentageExpression: expr }, true)
                            }}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.AGGREGATED_ELEMENTS:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.aggregatedElementsColumn)}>
                        <InlineNumberInput
                            value={line.unitPriceWithChildren}
                            className={'text-center'}
                            type="money"
                            decimalScale={decimalPlaces}
                            disabled
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.TOTAL_COST_UNIT_PRICE:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.totalCostUnitPriceColumn)}>
                        <InlineNumberInput
                            value={MathUtils.plus(line.grossUnitPrice, MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1, line.unitPriceWithChildren)}
                            className={'text-center'}
                            type="money"
                            decimalScale={decimalPlaces}
                            disabled
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.MARGIN:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.marginColumn)}>
                        <InlineNumberInput
                            value={line.marginPercentage}
                            onBlur={v => onChangeValue({ marginPercentage: v ?? 0, marginPercentageExpression: v !== line.marginPercentage ? null : line.marginPercentageExpression }, true)}
                            className={'text-center'}
                            type="percentage"
                            disabled={disabled}
                            expressionButton={{
                                title: ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.margin')]),
                                value: line.marginPercentageExpression ?? '',
                                loadParameters: () => getExpressionParameters(line.key),
                                onChange: (expr, value) => onChangeValue({ marginPercentage: value ?? 0, marginPercentageExpression: expr }, true)
                            }}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.DECENNIAL_INSURANCE:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.decennialInsuranceColumn)}>
                        <InlineNumberInput
                            value={line.decennialInsurancePercentage}
                            onBlur={v => onChangeValue({ decennialInsurancePercentage: v ?? 0, decennialInsurancePercentageExpression: v !== line.decennialInsurancePercentage ? null : line.decennialInsurancePercentageExpression }, true)}
                            className={'text-center'}
                            type="percentage"
                            disabled={disabled}
                            expressionButton={{
                                title: ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.decennial_insurance')]),
                                value: line.decennialInsurancePercentageExpression ?? '',
                                loadParameters: () => getExpressionParameters(line.key),
                                onChange: (expr, value) => onChangeValue({ decennialInsurancePercentage: value ?? 0, decennialInsurancePercentageExpression: expr }, true)
                            }}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.UNIT_PRICE:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.pricePerUnityColumn)}>
                        <InlineNumberInput
                            value={line.unitPrice}
                            className={'text-center'}
                            type="money"
                            decimalScale={decimalPlaces}
                            disabled
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.TOTAL_EXCLUDING_TAX:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.totalWithoutTaxColumn)}>
                        <CustomTotalCell
                            total={line.totalExcludingTax}
                            customTotalText={line.customTotalText}
                            customTotalTextUserName={line.customTotalTextUserName}
                            decimalPlaces={decimalPlaces}
                            disabled={disabled}
                            onChangeCustomText={v => onChangeValue({ customTotalText: v }, true)}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.TAX:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.taxColumn)}>
                        <InlineDropdownInput
                            value={line.taxId}
                            options={taxesOptions}
                            onChange={v => onChangeValue({ taxId: v, taxValue: taxes.find(x => x.id === v)?.value ?? 0 }, true)}
                            className={'text-center'}
                            disabled={disabled}
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.TOTAL_INCLUDING_TAX:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.totalWithTaxColumn)}>
                        {line.customTotalText
                            ? <span className={customTotalStyles.mutedTotal}>—</span>
                            : <InlineNumberInput
                                value={line.totalIncludingTax}
                                className={'text-center'}
                                type="money"
                                decimalScale={decimalPlaces}
                                disabled
                            />
                        }
                    </BudgetLineColumn>
                );
            case LinesTableColumn.NET_PROFIT:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.netProfitColumn)}>
                        <InlineNumberInput
                            value={MathUtils.multiply(MathUtils.minus(line.unitPrice, MathUtils.plus(line.grossUnitPrice, MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1, line.unitPriceWithChildren)), line.quantity ?? 0)}
                            className={'text-center'}
                            type="money"
                            decimalScale={decimalPlaces}
                            disabled
                        />
                    </BudgetLineColumn>
                );
            case LinesTableColumn.NET_PROFIT_PERCENTAGE:
                return (
                    <BudgetLineColumn key={col} column={col} className={cellClass(utilsStyles.netProfitPercentageColumn)}>
                        <InlineNumberInput
                            value={line.totalExcludingTax !== 0 ? MathUtils.multiply(MathUtils.divide(MathUtils.multiply(MathUtils.minus(line.unitPrice, MathUtils.plus(line.grossUnitPrice, MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1, line.unitPriceWithChildren)), line.quantity ?? 0), line.totalExcludingTax), 100) : 0}
                            className={'text-center'}
                            type="percentage"
                            decimalScale={decimalPlaces}
                            disabled
                        />
                    </BudgetLineColumn>
                );
            default:
                return null;
        }
    };

    const renderColumns = () => (
        <div className={styles.columnsContainer}>
            <div className={styles.columnsInnerContainer}>
                {renderCell(LinesTableColumn.DESIGNATION)}
                {orderedColumns.map(col => renderCell(col))}
            </div>
            {tabsOpen && (
                <div>
                    <SmallTabs activeKey={currentTab} onSelect={(k) => setCurrentTab(k)}>
                        <Tab eventKey={'configure'} title={t('budgets.budget.lines.tabs.configure')}>
                            <ConfigureTab line={line} disabled={disabled} highlightParameterKeys={highlightParameterKeys} />
                        </Tab>
                        <Tab eventKey={'description'} title={t('budgets.budget.lines.tabs.description')}>
                            <DescriptionTab line={line} disabled={disabled} />
                        </Tab>
                        {hasTreatmentSections && (
                            <Tab eventKey={'treatments'} title={t('budgets.budget.lines.tabs.treatments')}>
                                <TreatmentsTab line={line} disabled={disabled} isComponentElement={isComponentElement || isComponent} />
                            </Tab>
                        )}
                        <Tab eventKey={'prices'} title={t('budgets.budget.lines.tabs.price')}>
                            <PricesTab line={line} disabled={disabled} />
                        </Tab>
                        <Tab eventKey={'attachments'} title={t('budgets.budget.lines.tabs.attachments')}>
                            <AttachmentsTab line={line} disabled={disabled} />
                        </Tab>
                    </SmallTabs>
                </div>
            )}
        </div>
    );

    const elementTypeOptions = (elementTypes ?? []).map(et => ({ label: et.name, value: et.id }));

    const onAddComponentFromFamily = async () => {
        const budget = store.getState().budget.budget;

        let parameters: BudgetLineParameterDto[] = [];
        let allFamilyParams: ParameterSectionBaseModel[] = [];
        let familyDescription: string | undefined;
        if (line.tempFamily?.value) {
            try {
                setIsLoading(() => true);
                const [params, famParams, familyData] = await Promise.all([
                    getFamilyParametersForCustomElement(line.tempFamily.value),
                    FamiliesService.getFamilyParameters(line.tempFamily.value),
                    FamiliesService.getById(line.tempFamily.value),
                ]);
                parameters = params;
                allFamilyParams = famParams;
                familyDescription = familyData.description;
            } catch (error: any) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get family parameters', error);
                Toast.error(t('messages.error_load_info'));
            } finally {
                setIsLoading(() => false);
            }
        }

        const lineObj: Partial<BudgetLineDto> = {
            elementId: null,
            designation: customElementName,
            subDesignation: line.tempFamily?.label ?? '',
            grossUnitPrice: 0,
            unitId: null,
            taxId: budget.defaultTaxId ? budget.defaultTaxId : null,
            taxValue: budget.defaultTaxValue ? budget.defaultTaxValue : 0,
            discountPercentage: budget.defaultDiscountPercentage ? budget.defaultDiscountPercentage : 0,
            description: familyDescription || undefined,
            parameters,
            checkedForParameters: true,
            changedParameters: parameters.length > 0,
            isCustomElement: true,
            pdfVisibility: line.lineType === BudgetLineType.ELEMENT
                ? BudgetPDFVisibility.PRICE_INVISIBLE
                : BudgetPDFVisibility.ELEMENT_AND_PRICE_VISIBLE,
        };

        const allLines: BudgetLineDto[] = store.getState().budget.lines;
        const currentLine = allLines.find(l => l.key === line.key);
        if (currentLine && isTopLevelElementLine(currentLine, allLines)) {
            lineObj.discountPercentageExpression = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['budget', 'defaultDiscountPercentage']));
            lineObj.marginPercentageExpression = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['budget', 'defaultCombinedMarginPercentage']));
            lineObj.marginPercentage = (budget.defaultMarginPercentage ?? 0) + (budget.defaultCommissionMarginPercentage ?? 0);
            lineObj.decennialInsurancePercentageExpression = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['budget', 'defaultDecennialInsurancePercentage']));
            lineObj.decennialInsurancePercentage = budget.defaultDecennialInsurancePercentage ?? 0;
        }

        onChangeValue(lineObj, false);
        dispatch(calculateLineTotals(line.key));

        // Create child SECTION lines for PRODUCTS parameters
        const productsParams = allFamilyParams.filter(p => p.parameterType === ParameterSectionParameterType.PRODUCTS);
        for (const productParam of productsParams) {
            const childKey = Utils.newGuid();
            dispatch(addLine(BudgetLineType.SECTION, 1, line.key, childKey));
            dispatch(updateLine({
                key: childKey,
                designation: productParam.parameterName || productParam.title || '',
                sourceFamilyParameterId: productParam.id || null,
                sourceRequiresTreatment: productParam.requiresTreatment ?? false,
            }));
        }

        onAddedElement();
    }

    const renderEditionMode = () => {
        if (isComponent || isComponentElement) {
            return (
                <Row className={`${styles.editionRow} align-items-end`}>
                    <Col xs={12} sm={3}>
                        <Label space>{t('budgets.budget.lines.ouvrage_family_subfamily')}{'*'}</Label>
                        <SearchableSelectInput
                            value={line.tempFamily?.value}
                            loadOptions={onLoadFamilies}
                            onChange={onChangeFamily}
                            formatOptionLabel={OptionTitleSubTitle}
                            noFilter={true}
                        />
                    </Col>
                    <Col xs={12} sm>
                        <Label space>{t('budgets.budget.lines.ouvrage_name')}{'*'}</Label>
                        <Form.Control
                            type="text"
                            className={styles.elementNameInput}
                            value={customElementName}
                            onChange={(e) => setCustomElementName(e.target.value)}
                            placeholder={t('budgets.budget.lines.ouvrage_name_placeholder')}
                        />
                    </Col>
                    <Col xs={12} sm={'auto'} className={styles.editionButtons}>
                        <Button className={styles.editionAddButton} disabled={!customElementName || !line.tempFamily?.value} onClick={onAddComponentFromFamily}>
                            {t('common.ok')}
                        </Button>
                    </Col>
                </Row>
            );
        }

        return (
            <Row className={`${styles.editionRow} align-items-end`}>
                <Col xs={12} sm={3}>
                    <Label space>{isComponentElement ? t('budgets.budget.lines.ouvrage_family_subfamily') : t('budgets.budget.lines.element_family_subfamily')}</Label>
                    <SearchableSelectInput
                        value={line.tempFamily?.value}
                        loadOptions={onLoadFamilies}
                        onChange={onChangeFamily}
                        formatOptionLabel={OptionTitleSubTitle}
                        noFilter={true}
                    />
                </Col>
                {!isCreateCustom && (
                    <Col xs={12} sm>
                        <Label space>{isComponentElement ? t('budgets.budget.lines.ouvrage') : t('budgets.budget.lines.element')}{'*'}</Label>
                        <SearchableSelectInput
                            value={line.tempElement?.value}
                            loadOptions={onLoadElements}
                            onChange={onChangeElement}
                            onClickGoToDetails={() => {
                                if (line.tempElement?.value) {
                                    window.open('/library/elements-items/details/' + line.tempElement?.value);
                                }
                            }}
                            formatOptionLabel={OptionTitleSubTitle}
                            key={line.tempFamily?.value + '-elements'}
                            noFilter={true}
                        />
                    </Col>
                )}
                {isCreateCustom && (
                    <>
                        <Col xs={12} sm={3}>
                            <Label space>{t('budgets.budget.lines.element_type')}{'*'}</Label>
                            <SelectInput
                                value={customElementTypeId}
                                options={elementTypeOptions}
                                onChange={(v) => setCustomElementTypeId(v ?? null)}
                            />
                        </Col>
                        <Col xs={12} sm>
                            <Label space>{t('budgets.budget.lines.element_name')}{'*'}</Label>
                            <Form.Control
                                type="text"
                                className={styles.elementNameInput}
                                value={customElementName}
                                onChange={(e) => setCustomElementName(e.target.value)}
                                placeholder={t('budgets.budget.lines.element_name_placeholder')}
                            />
                        </Col>
                    </>
                )}
                <Col xs={12} sm={'auto'} className={styles.editionButtons}>
                    <Button className={styles.editionAddButton} disabled={isCreateCustom ? (!customElementTypeId || !customElementName) : !line.tempElement} onClick={onAddElement}>
                        {t('common.ok')}
                    </Button>
                    <span className={styles.editionOrLabel}>{t('budgets.budget.lines.or')}</span>
                    <Button
                        className={`${styles.editionCreateButton} ${isCreateCustom ? styles.editionCreateButtonActive : ''}`}
                        variant="secondary"
                        disabled={!!line.tempElement}
                        onClick={onToggleCreateCustom}
                    >
                        {isCreateCustom ? t('budgets.budget.lines.select_element') : t('budgets.budget.lines.create_element')}
                    </Button>
                </Col>
            </Row>
        );
    };

    return (
        <div
            className={styles.container}
            ref={ref}
        >
            <div className={styles.element}>
                {isLoading && <LoadingSpinnerCenter />}
                {isEditing && !isLoading && renderEditionMode()}
                {!isEditing && !isLoading && renderColumns()}
            </div>

            <InformationModal
                message={t('budgets.budget.lines.has_parameters_quantities_info', {
                    parameterName: quantityParameters.map(p => p.name).join(', ')
                })}
                onClose={() => {
                    setShowParametersQuantityInformation(false);
                    setCurrentTab('configure');
                    setHighlightParameterKeys(quantityParameters.map(p => p.key));
                }}
                isOpen={showParametersQuantityInformation}
            />
            <YesNoModal
                title={t('budgets.budget.lines.keep_formulas_title')}
                message={t('budgets.budget.lines.keep_formulas_message')}
                isOpen={keepFormulasPromise !== null}
                onClose={(res) => {
                    keepFormulasPromise?.resolve(res);
                    setKeepFormulasPromise(null);
                }}
            />
        </div>
    );
}

export default forwardRef<HTMLDivElement, Props>(BudgetElementLine);
